import { useEffect, useRef, useState } from "react";
import { useCabinetSubscription, useCurrentCabinetId, type CabinetSubscription, type LicenseUsage } from "@/hooks/useCabinetSubscription";
import { useLicenseExempt } from "@/hooks/useLicenseExempt";

export const useUnifiedSubscription = () => {
  // ALL HOOKS MUST BE CALLED UNCONDITIONALLY AT THE TOP
  const { isLicenseExempt, isLoading: isLicenseExemptLoading } = useLicenseExempt();
  const currentCabinetId = useCurrentCabinetId();
  const cabinetSub = useCabinetSubscription(currentCabinetId ?? undefined);

  // Stripe webhook delay state — must be at top level (Rules of Hooks)
  const [isWaitingForWebhook, setIsWaitingForWebhook] = useState(false);
  const webhookDelayStartRef = useRef<number | null>(null);
  // Stable ref for refetch — cabinetSub.refetch is a new function every render
  const refetchRef = useRef(cabinetSub.refetch);
  refetchRef.current = cabinetSub.refetch;

  const isReturningFromStripe = (() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('success') || urlParams.has('canceled') ||
           !!urlParams.get('session_id') ||
           sessionStorage.getItem('returning_from_stripe') === 'true';
  })();

  const needsWebhookDelay = !cabinetSub.isLoading && !!currentCabinetId &&
    isReturningFromStripe &&
    cabinetSub.subscription?.status === 'trialing' &&
    cabinetSub.isFirstTimeInCabinet;

  useEffect(() => {
    if (!needsWebhookDelay) {
      if (webhookDelayStartRef.current !== null) {
        webhookDelayStartRef.current = null;
        setIsWaitingForWebhook(false);
        sessionStorage.removeItem('stripe_webhook_delay_started');
        sessionStorage.removeItem('returning_from_stripe');
      }
      return;
    }

    if (webhookDelayStartRef.current === null) {
      const existing = sessionStorage.getItem('stripe_webhook_delay_started');
      const start = existing ? parseInt(existing) : Date.now();
      if (!existing) {
        sessionStorage.setItem('stripe_webhook_delay_started', start.toString());
        sessionStorage.setItem('returning_from_stripe', 'true');
      }
      webhookDelayStartRef.current = start;
      setIsWaitingForWebhook(true);
    }

    const interval = setInterval(() => {
      const elapsed = Date.now() - (webhookDelayStartRef.current ?? Date.now());
      if (elapsed > 5000) {
        clearInterval(interval);
        webhookDelayStartRef.current = null;
        setIsWaitingForWebhook(false);
        sessionStorage.removeItem('stripe_webhook_delay_started');
        sessionStorage.removeItem('returning_from_stripe');
        return;
      }
      refetchRef.current();
    }, 1000);

    return () => clearInterval(interval);
  }, [needsWebhookDelay]);
  
  // Staff loading state - return loading while checking staff status
  if (isLicenseExemptLoading) {
    // CAST: type-widen empty literals for return type inference
    return {
      hasValidSubscription: true, // Assume valid while loading to prevent dialogs
      isTrialing: false,
      isActive: false,
      isTrialEndingSoon: false,
      isTrialExpired: false,
      isNewUser: false,
      isLicenseExempt: false, // Don't set to true while loading
      redirectToCheckout: () => {},
      completeOnboarding: async () => true,
      isLoading: true, // Show loading state
      loading: true,
      onboardingLoading: false,
      subscription: null as CabinetSubscription | null,
      subscriptionType: 'loading' as const,
      cabinetId: null as string | null,
      licenseUsage: null as LicenseUsage | null,
      isAdmin: false,
      
      // No dialog states while loading
      hasJustUpgraded: false,
      isFirstTimeInCabinet: false,
      markCabinetAsVisited: () => {},
    };
  }

  if (isLicenseExempt && !isLicenseExemptLoading) {
    // CAST: type-widen empty literals for return type inference
    return {
      hasValidSubscription: true, // Staff always has valid access
      isTrialing: false,
      isActive: true,
      isTrialEndingSoon: false,
      isTrialExpired: false,
      isNewUser: false, // Staff never see pricing
      isLicenseExempt: true,
      redirectToCheckout: () => {},
      completeOnboarding: async () => true,
      isLoading: false,
      loading: false,
      onboardingLoading: false,
      subscription: null as CabinetSubscription | null,
      subscriptionType: 'staff' as const,
      cabinetId: null as string | null,
      licenseUsage: null as LicenseUsage | null,
      isAdmin: false,
      
      // Staff don't need dialog states
      hasJustUpgraded: false,
      isFirstTimeInCabinet: false,
      markCabinetAsVisited: () => {},
    };
  }

  // SMARTER DETECTION: Only disable during actual profile completion page, not after dashboard redirect
  const isOnProfileCompletionPage = window.location.pathname.includes('complete-profile');
  const isInActiveInviteFlow = window.location.search.includes('cabinetId=') && window.location.pathname.includes('complete-profile');
  
  // Check if user is currently in invitation flow (not just came from it)
  const redirectAfterLogin = typeof window !== 'undefined' ? sessionStorage.getItem('redirectAfterLogin') : null;
  const isCurrentlyInInviteFlow = redirectAfterLogin && redirectAfterLogin.includes('/invite/') && window.location.pathname.includes('invite');
  
  // Only disable if user is CURRENTLY on profile completion page or actively in invite flow
  if (isOnProfileCompletionPage || isInActiveInviteFlow || isCurrentlyInInviteFlow) {
    // CAST: type-widen empty literals for return type inference
    return {
      hasValidSubscription: true, // Assume valid during invitation
      isTrialing: false,
      isActive: true,
      isTrialEndingSoon: false,
      isTrialExpired: false,
      isNewUser: false, // Don't mark as new user during invitation flow
      isLicenseExempt: false,
      redirectToCheckout: () => {},
      completeOnboarding: async () => true,
      isLoading: false,
      loading: false,
      onboardingLoading: false,
      subscription: null as CabinetSubscription | null,
      subscriptionType: 'cabinet' as const,
      cabinetId: null as string | null,
      licenseUsage: null as LicenseUsage | null,
      isAdmin: false,

      // Profile completion doesn't need cabinet-specific dialog states
      hasJustUpgraded: false,
      isFirstTimeInCabinet: false,
      markCabinetAsVisited: () => {},
    };
  }
  
  // Intelligent pricing page logic based on database fields
  if (!cabinetSub.isLoading && currentCabinetId) {
    const { subscription, isFirstTimeInCabinet } = cabinetSub;

    // Still waiting for Stripe webhook — show loading state
    if (isWaitingForWebhook) {
      return {
        hasValidSubscription: true,
        isTrialing: false,
        isActive: true,
        isTrialEndingSoon: false,
        isTrialExpired: false,
        isNewUser: false,
        isLicenseExempt: false,
        redirectToCheckout: () => {},
        completeOnboarding: async () => true,
        isLoading: true,
        loading: true,
        onboardingLoading: false,
        subscription: subscription,
        subscriptionType: 'cabinet' as const,
        cabinetId: currentCabinetId,
        licenseUsage: cabinetSub.licenseUsage,
        isAdmin: cabinetSub.isAdmin,
        hasJustUpgraded: false,
        isFirstTimeInCabinet,
        markCabinetAsVisited: cabinetSub.markCabinetAsVisited,
      };
    }
    
    // Determine if user should see pricing page - use SAME logic as usePricingPageState
    const shouldShowPricing = subscription?.status === 'trialing' && isFirstTimeInCabinet;
    
    // CORRECT LOGIC: Check actual subscription validity
    // For trialing users who are first-time visitors, they should NOT have valid subscription
    // so they get redirected to pricing page
    const actualHasValidSubscription = (
      subscription?.status === 'active' || 
      (subscription?.status === 'trialing' && !isFirstTimeInCabinet)
    );
    
    return {
      hasValidSubscription: actualHasValidSubscription, // Use corrected logic
      isTrialing: subscription?.status === 'trialing',
      isActive: subscription?.status === 'active',
      isTrialEndingSoon: false, // Will be calculated elsewhere if needed
      isTrialExpired: false,
      isNewUser: shouldShowPricing, // Only true for trialing + first time
      isLicenseExempt: false,
      redirectToCheckout: () => {},
      completeOnboarding: async () => true,
      isLoading: false,
      loading: false,
      onboardingLoading: false,
      subscription: subscription,
      subscriptionType: 'cabinet' as const,
      cabinetId: currentCabinetId,
      licenseUsage: cabinetSub.licenseUsage,
      isAdmin: cabinetSub.isAdmin,
      
      // Cabinet-specific dialog states
      hasJustUpgraded: false,
      isFirstTimeInCabinet,
      markCabinetAsVisited: cabinetSub.markCabinetAsVisited,
    };
  }
  
  // Determine which subscription system to use
  // All users use cabinet-level logic
  const shouldUseCabinetBilling = currentCabinetId && cabinetSub.isAdmin;
  const hasCabinetSubscription = cabinetSub.subscription || cabinetSub.isNewCabinet;
  
  // Note: Staff check moved to the beginning of the function
  
  // Use cabinet subscription if user is a cabinet member (prioritize over individual)
  if (currentCabinetId && (shouldUseCabinetBilling || hasCabinetSubscription)) {
    return {
      hasValidSubscription: cabinetSub.hasValidSubscription,
      isTrialing: cabinetSub.isTrialing,
      isActive: cabinetSub.isActive,
      isTrialEndingSoon: cabinetSub.isTrialEndingSoon,
      isTrialExpired: cabinetSub.isTrialExpired,
      isNewUser: cabinetSub.isNewCabinet,
      isLicenseExempt: false,
      
      // Price id désormais résolu côté backend — on ne transmet que la quantité.
      redirectToCheckout: (quantity?: number) =>
        cabinetSub.redirectToCabinetCheckout(quantity || cabinetSub.licenseUsage?.max_licenses || 5),
      completeOnboarding: cabinetSub.completeCabinetOnboarding,
      markOnboardingComplete: cabinetSub.markOnboardingComplete,
      markCabinetAsVisited: cabinetSub.markCabinetAsVisited,
      
      isLoading: cabinetSub.isLoading,
      loading: cabinetSub.checkoutLoading,
      onboardingLoading: cabinetSub.isCreatingCustomer,
      
      subscription: cabinetSub.subscription,
      subscriptionType: 'cabinet' as const,
      
      // Cabinet-specific data
      cabinetId: currentCabinetId,
      licenseUsage: cabinetSub.licenseUsage,
      isAdmin: cabinetSub.isAdmin,
      
      // Cabinet-specific dialog states
      hasJustUpgraded: cabinetSub.hasJustUpgraded,
      isFirstTimeInCabinet: cabinetSub.isFirstTimeInCabinet,
    };
  }
  
  // All users must have cabinet membership - no individual subscription fallback
  
  // Default case: user has cabinet membership but no valid subscription data yet
  // CAST: type-widen empty literals for return type inference
  return {
    hasValidSubscription: false,
    isTrialing: false,
    isActive: false,
    isTrialEndingSoon: false,
    isTrialExpired: false,
    isNewUser: true, // Show as new user to trigger pricing page
    isLicenseExempt: false,

    redirectToCheckout: () => {},
    completeOnboarding: async () => true,

    isLoading: cabinetSub.isLoading,
    loading: false,
    onboardingLoading: false,

    subscription: null as CabinetSubscription | null,
    subscriptionType: 'cabinet' as const,

    // No cabinet-specific dialog states for default case
    hasJustUpgraded: false,
    isFirstTimeInCabinet: false,
    markCabinetAsVisited: () => {},
  };
};
